import { useCallback, useRef, useEffect, RefObject } from 'react'

interface ScrollSyncOptions {
    enabled?: boolean
}

interface ScrollSyncReturn {
    leftRef: RefObject<HTMLElement>
    rightRef: RefObject<HTMLElement>
}

export const useScrollSync = (options: ScrollSyncOptions = {}): ScrollSyncReturn => {
    const { enabled = true } = options

    const leftRef = useRef<HTMLElement>(null)
    const rightRef = useRef<HTMLElement>(null)
    const isScrollingLeftRef = useRef(false)
    const isScrollingRightRef = useRef(false)
    const leftScrollTimeoutRef = useRef<NodeJS.Timeout>()

    const getCursorLinePosition = useCallback((textarea: HTMLTextAreaElement): number => {
        const cursorPosition = textarea.selectionStart
        const textBeforeCursor = textarea.value.substring(0, cursorPosition)
        return textBeforeCursor.split('\n').length - 1
    }, [])

    const findContentBlockAtLine = useCallback((content: string, lineNumber: number) => {
        const lines = content.split('\n')
        if (lineNumber >= lines.length) return null

        const currentLine = lines[lineNumber].trim()

        if (currentLine === '') {
            for (let i = lineNumber + 1; i < lines.length; i++) {
                if (lines[i].trim() !== '') {
                    lineNumber = i
                    break
                }
            }
        }

        let blockStart = lineNumber
        let blockEnd = lineNumber
        const targetLine = lines[lineNumber].trim()

        let blockType = 'paragraph'
        if (targetLine.match(/^#{1,6}\s/)) {
            blockType = 'heading'
        } else if (targetLine.match(/^[-*+]\s/) || targetLine.match(/^\d+\.\s/)) {
            blockType = 'list'
        } else if (targetLine.startsWith('>')) {
            blockType = 'blockquote'
        } else if (targetLine.startsWith('```') || targetLine.startsWith('~~~')) {
            blockType = 'code'
        }

        if (blockType === 'heading') {
            return {
                startLine: lineNumber,
                endLine: lineNumber,
                content: lines[lineNumber]
            }
        }

        if (blockType === 'code') {
            const delimiter = targetLine.startsWith('```') ? '```' : '~~~'
            for (let i = lineNumber + 1; i < lines.length; i++) {
                if (lines[i].trim().startsWith(delimiter)) {
                    blockEnd = i
                    break
                }
            }
        } else {
            for (let i = lineNumber - 1; i >= 0; i--) {
                const line = lines[i].trim()
                if (line === '') {
                    blockStart = i + 1
                    break
                }

                let lineType = 'paragraph'
                if (line.match(/^#{1,6}\s/)) lineType = 'heading'
                else if (line.match(/^[-*+]\s/) || line.match(/^\d+\.\s/)) lineType = 'list'
                else if (line.startsWith('>')) lineType = 'blockquote'

                if (lineType !== blockType && lineType !== 'paragraph') {
                    blockStart = i + 1
                    break
                }

                if (i === 0) {
                    blockStart = 0
                    break
                }
            }

            for (let i = lineNumber + 1; i < lines.length; i++) {
                const line = lines[i].trim()
                if (line === '') {
                    blockEnd = i - 1
                    break
                }

                let lineType = 'paragraph'
                if (line.match(/^#{1,6}\s/)) lineType = 'heading'
                else if (line.match(/^[-*+]\s/) || line.match(/^\d+\.\s/)) lineType = 'list'
                else if (line.startsWith('>')) lineType = 'blockquote'

                if (lineType !== blockType && lineType !== 'paragraph') {
                    blockEnd = i - 1
                    break
                }

                if (i === lines.length - 1) {
                    blockEnd = i
                    break
                }
            }
        }

        const blockContent = lines.slice(blockStart, blockEnd + 1).join('\n').trim()
        if (!blockContent) return null

        return {
            startLine: blockStart,
            endLine: blockEnd,
            content: blockContent
        }
    }, [])

    const findBlockInPreview = useCallback((preview: HTMLElement, blockContent: string) => {
        const isListItem = blockContent.match(/^[-*+]\s+/) || blockContent.match(/^\d+\.\s+/)
        const hasMultipleListItems = (blockContent.match(/^[-*+]\s+/gm) || blockContent.match(/^\d+\.\s+/gm) || []).length > 1

        const isHeading = blockContent.match(/^#{1,6}\s+/)

        let cleanedContent = blockContent
            .replace(/^#{1,6}\s+/, '')
            .replace(/^[-*+]\s+/, '')
            .replace(/^\d+\.\s+/, '')
            .replace(/^>\s*/gm, '')
            .replace(/```[\s\S]*?```/g, '')
            .replace(/`[^`]+`/g, '')
            .replace(/\*\*([^*]+)\*\*/g, '$1')
            .replace(/\*([^*]+)\*/g, '$1')
            .trim()

        let headingTextOnly = ''
        if (isHeading) {
            headingTextOnly = cleanedContent
                .replace(/^\d+\.\s*/, '')
                .replace(/^[A-Z]+\.\s*/, '')
                .replace(/^\([0-9]+\)\s*/, '')
                .replace(/^[0-9]+\s*-\s*/, '')
                .trim()
        }

        if (cleanedContent.length < 2) return null

        let words = cleanedContent.split(/\s+/).slice(0, 8).join(' ').toLowerCase()
        const headingWords = headingTextOnly ? headingTextOnly.split(/\s+/).slice(0, 8).join(' ').toLowerCase() : ''

        if (hasMultipleListItems) {
            const firstListItem = blockContent.split('\n')[0]
                .replace(/^[-*+]\s+/, '')
                .replace(/^\d+\.\s+/, '')
                .trim()
            words = firstListItem.split(/\s+/).slice(0, 6).join(' ').toLowerCase()
        }

        let elementSelector = 'p, h1, h2, h3, h4, h5, h6, li, blockquote, pre, code'
        if (isListItem) {
            if (hasMultipleListItems) {
                elementSelector = 'ul, ol, li, p, h1, h2, h3, h4, h5, h6, blockquote, pre, code'
            } else {
                elementSelector = 'li, ul, ol, p, h1, h2, h3, h4, h5, h6, blockquote, pre, code'
            }
        } else if (isHeading) {
            elementSelector = 'h1, h2, h3, h4, h5, h6, p, li, blockquote, pre, code'
        }

        const elements = preview.querySelectorAll(elementSelector)
        let bestMatch: HTMLElement | null = null
        let bestScore = 0

        for (const element of elements) {
            const elementText = element.textContent?.toLowerCase().trim() || ''

            if (elementText.length < 2) continue

            let baseScore = 1.0
            if (isListItem) {
                if (hasMultipleListItems && (element.tagName === 'UL' || element.tagName === 'OL')) {
                    baseScore = 2.0
                } else if (!hasMultipleListItems && element.tagName === 'LI') {
                    baseScore = 1.5
                } else if (element.tagName === 'LI') {
                    baseScore = 1.2
                }
            } else if (isHeading && element.tagName.match(/^H[1-6]$/)) {
                baseScore = 1.5
            }

            if (elementText === cleanedContent.toLowerCase()) {
                return element as HTMLElement
            }

            if (isHeading && headingWords && elementText === headingWords) {
                return element as HTMLElement
            }

            if (elementText.startsWith(words)) {
                const score = (words.length / elementText.length) * baseScore
                if (score > bestScore) {
                    bestScore = score
                    bestMatch = element as HTMLElement
                }
            }

            if (isHeading && headingWords && elementText.startsWith(headingWords)) {
                const score = (headingWords.length / elementText.length) * baseScore * 1.1
                if (score > bestScore) {
                    bestScore = score
                    bestMatch = element as HTMLElement
                }
            }

            if (words.startsWith(elementText) && elementText.length > 3) {
                const score = (elementText.length / words.length) * baseScore
                if (score > bestScore) {
                    bestScore = score
                    bestMatch = element as HTMLElement
                }
            }

            const testWords = [words]
            if (isHeading && headingWords) {
                testWords.push(headingWords)
            }

            for (const testWord of testWords) {
                const elementWords = elementText.split(/\s+/)
                const contentWords = testWord.split(/\s+/)
                let matchingWords = 0

                for (let i = 0; i < Math.min(elementWords.length, contentWords.length); i++) {
                    if (elementWords[i] === contentWords[i]) {
                        matchingWords++
                    } else {
                        break
                    }
                }

                if (matchingWords >= 2) {
                    const headingBonus = (testWord === headingWords) ? 1.2 : 1.0
                    const score = (matchingWords / Math.max(elementWords.length, contentWords.length)) * baseScore * headingBonus
                    if (score > bestScore) {
                        bestScore = score
                        bestMatch = element as HTMLElement
                    }
                }
            }

            if (elementText.includes(words.substring(0, 10)) && words.length > 5) {
                const score = 0.3 * baseScore
                if (score > bestScore) {
                    bestScore = score
                    bestMatch = element as HTMLElement
                }
            }
        }

        return bestMatch
    }, [])

    const isContentVisibleInPreview = useCallback((preview: HTMLElement, blockContent: string): boolean => {
        if (!blockContent || blockContent.length < 3) return false

        const previewScrollTop = preview.scrollTop
        const previewHeight = preview.clientHeight
        const previewBottom = previewScrollTop + previewHeight

        const targetElement = findBlockInPreview(preview, blockContent)
        if (!targetElement) return false

        const elementTop = targetElement.offsetTop
        const elementBottom = elementTop + targetElement.offsetHeight

        const isVisible = elementTop < previewBottom && elementBottom > previewScrollTop

        return isVisible
    }, [findBlockInPreview])

    const syncEditorToPreview = useCallback((editor: HTMLElement, preview: HTMLElement, content: string) => {
        const textarea = editor.querySelector('textarea') as HTMLTextAreaElement
        if (!textarea) return

        const cursorLine = getCursorLinePosition(textarea)
        const block = findContentBlockAtLine(content, cursorLine)

        if (block) {
            if (isContentVisibleInPreview(preview, block.content)) {
                return
            }

            const targetElement = findBlockInPreview(preview, block.content)
            if (targetElement) {
                const elementTop = targetElement.offsetTop
                const targetScroll = Math.max(0, elementTop - 50)

                isScrollingRightRef.current = true
                preview.scrollTop = targetScroll

                setTimeout(() => {
                    isScrollingRightRef.current = false
                }, 100)
                return
            }
        }

        const totalLines = content.split('\n').length
        if (totalLines > 0) {
            const scrollRatio = cursorLine / totalLines
            const currentPreviewRatio = preview.scrollTop / (preview.scrollHeight - preview.clientHeight)

            if (Math.abs(scrollRatio - currentPreviewRatio) < 0.1) {
                return
            }

            const previewScrollableHeight = preview.scrollHeight - preview.clientHeight
            if (previewScrollableHeight > 0) {
                isScrollingRightRef.current = true
                preview.scrollTop = scrollRatio * previewScrollableHeight

                setTimeout(() => {
                    isScrollingRightRef.current = false
                }, 100)
            }
        }
    }, [getCursorLinePosition, findContentBlockAtLine, isContentVisibleInPreview, findBlockInPreview])



    const findTableRowInEditor = useCallback((content: string, rowText: string) => {
        const lines = content.split('\n')
        const cleanedRowText = rowText
            .replace(/\s+/g, ' ')
            .trim()
            .toLowerCase()

        let bestMatch: { startLine: number; endLine: number } | null = null
        let bestScore = 0

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i].trim()

            if (line.includes('|') && !line.startsWith('|--') && !line.startsWith('| --')) {
                const cleanedLine = line
                    .replace(/\|/g, ' ')
                    .replace(/\s+/g, ' ')
                    .trim()
                    .toLowerCase()

                if (cleanedLine.length < 3) continue

                const rowWords = cleanedRowText.split(/\s+/).filter(w => w.length > 2)
                const lineWords = cleanedLine.split(/\s+/).filter(w => w.length > 2)

                if (rowWords.length === 0 || lineWords.length === 0) continue

                let matchingWords = 0
                for (const rowWord of rowWords) {
                    if (lineWords.some(lineWord => lineWord.includes(rowWord) || rowWord.includes(lineWord))) {
                        matchingWords++
                    }
                }

                if (matchingWords > 0) {
                    const score = matchingWords / Math.max(rowWords.length, lineWords.length)
                    if (score > bestScore) {
                        bestScore = score
                        bestMatch = { startLine: i, endLine: i }
                    }
                }
            }
        }

        return bestMatch
    }, [])

    const findBlockInEditor = useCallback((content: string, targetText: string, isTable = false) => {
        if (isTable) {
            return findTableRowInEditor(content, targetText)
        }
        const lines = content.split('\n')

        const cleanedTarget = targetText
            .replace(/\s+/g, ' ')
            .trim()
            .toLowerCase()

        if (cleanedTarget.length < 3) return null

        const searchVariations = [
            cleanedTarget,
            cleanedTarget.replace(/^\d+\.\s*/, ''),
            cleanedTarget.replace(/^\d+(\.\d+)*\s*/, ''),
            cleanedTarget.replace(/\s+analysis$/, ''),
            cleanedTarget.replace(/^\d+\.\s*/, '').replace(/\s+analysis$/, ''),
        ].filter(v => v.length >= 3)

        let bestMatch: { startLine: number; endLine: number } | null = null
        let bestScore = 0

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i].trim()
            if (line === '') continue

            const cleanedLine = line
                .replace(/^#{1,6}\s+/, '')
                .replace(/^[-*+]\s+/, '')
                .replace(/^\d+\.\s+/, '')
                .replace(/^>\s*/, '')
                .replace(/`[^`]+`/g, '')
                .replace(/\*\*([^*]+)\*\*/g, '$1')
                .replace(/\*([^*]+)\*/g, '$1')
                .replace(/\s+/g, ' ')
                .trim()
                .toLowerCase()

            if (cleanedLine.length < 3) continue

            for (const searchText of searchVariations) {
                if (searchText.length < 3) continue

                if (cleanedLine === searchText) {
                    return { startLine: i, endLine: i }
                }

                if (cleanedLine.includes(searchText)) {
                    const score = searchText.length / cleanedLine.length
                    if (score > bestScore) {
                        bestScore = score
                        bestMatch = { startLine: i, endLine: i }
                    }
                }

                if (searchText.includes(cleanedLine) && cleanedLine.length > 5) {
                    const score = cleanedLine.length / searchText.length
                    if (score > bestScore) {
                        bestScore = score
                        bestMatch = { startLine: i, endLine: i }
                    }
                }

                const searchWords = searchText.split(/\s+/)
                const lineWords = cleanedLine.split(/\s+/)
                let matchingWords = 0

                for (let j = 0; j < Math.min(searchWords.length, lineWords.length); j++) {
                    if (searchWords[j] === lineWords[j]) {
                        matchingWords++
                    } else {
                        break
                    }
                }

                if (matchingWords >= 2) {
                    const score = matchingWords / Math.max(searchWords.length, lineWords.length)
                    if (score > bestScore) {
                        bestScore = score
                        bestMatch = { startLine: i, endLine: i }
                    }
                }
            }
        }

        return bestMatch
    }, [findTableRowInEditor])



    useEffect(() => {
        if (!enabled) return

        const leftElement = leftRef.current
        const rightElement = rightRef.current

        if (!leftElement || !rightElement) return

        const textarea = leftElement as HTMLTextAreaElement
        if (!textarea || textarea.tagName !== 'TEXTAREA') return

        const syncFromCursor = () => {
            if (!enabled || isScrollingLeftRef.current) return

            if (leftScrollTimeoutRef.current) {
                clearTimeout(leftScrollTimeoutRef.current)
            }

            leftScrollTimeoutRef.current = setTimeout(() => {
                const source = leftRef.current
                const target = rightRef.current

                if (!source || !target || isScrollingLeftRef.current) return

                const currentTextarea = source as HTMLTextAreaElement
                if (!currentTextarea || currentTextarea.tagName !== 'TEXTAREA') return

                const content = currentTextarea.value || ''
                const editorContainer = currentTextarea.closest('.custom-markdown-editor') as HTMLElement
                if (!editorContainer) return

                syncEditorToPreview(editorContainer, target, content)
            }, 50)
        }

        const leftScrollHandler = () => {
            if (!enabled || isScrollingLeftRef.current || isScrollingRightRef.current) return

            setTimeout(() => {
                if (isScrollingRightRef.current) return
                syncFromCursor()
            }, 100)
        }



        const previewClickHandler = (event: Event) => {
            if (!enabled || isScrollingRightRef.current) return

            const target = event.target as HTMLElement
            if (!target) return

            const contentElement = target.closest('h1, h2, h3, h4, h5, h6, p, li, blockquote, pre, code, table, tr, td, th') as HTMLElement
            if (!contentElement) return

            let elementText = contentElement.textContent || ''
            let isTableElement = false

            if (contentElement.tagName === 'TD' || contentElement.tagName === 'TH') {
                const parentRow = contentElement.closest('tr')
                if (parentRow) {
                    elementText = parentRow.textContent || ''
                    isTableElement = true
                }
            } else if (contentElement.tagName === 'TR') {
                elementText = contentElement.textContent || ''
                isTableElement = true
            } else if (contentElement.tagName === 'TABLE') {
                const firstRow = contentElement.querySelector('tr')
                if (firstRow) {
                    elementText = firstRow.textContent || ''
                    isTableElement = true
                }
            }

            if (elementText.length < 3) return

            const matchingBlock = findBlockInEditor(textarea.value, elementText, isTableElement)
            if (matchingBlock) {
                const lineHeight = parseInt(getComputedStyle(textarea).lineHeight) || 21
                const targetScrollTop = matchingBlock.startLine * lineHeight

                const paddedScrollTop = Math.max(0, targetScrollTop - lineHeight * 3)

                isScrollingRightRef.current = true
                textarea.scrollTop = paddedScrollTop

                const lines = textarea.value.split('\n')
                let charPosition = 0
                for (let i = 0; i < matchingBlock.startLine; i++) {
                    charPosition += lines[i].length + 1
                }

                const targetLineLength = lines[matchingBlock.startLine].length
                const endOfLinePosition = charPosition + targetLineLength

                textarea.setSelectionRange(endOfLinePosition, endOfLinePosition)
                textarea.focus()

                setTimeout(() => {
                    isScrollingRightRef.current = false
                }, 100)
            }
        }

        textarea.addEventListener('scroll', leftScrollHandler, { passive: true })
        textarea.addEventListener('click', syncFromCursor, { passive: true })
        textarea.addEventListener('keyup', syncFromCursor, { passive: true })
        textarea.addEventListener('input', syncFromCursor, { passive: true })
        rightElement.addEventListener('click', previewClickHandler, { passive: true })

        return () => {
            textarea.removeEventListener('scroll', leftScrollHandler)
            textarea.removeEventListener('click', syncFromCursor)
            textarea.removeEventListener('keyup', syncFromCursor)
            textarea.removeEventListener('input', syncFromCursor)
            rightElement.removeEventListener('click', previewClickHandler)

            if (leftScrollTimeoutRef.current) {
                clearTimeout(leftScrollTimeoutRef.current)
            }
        }
    }, [enabled, syncEditorToPreview, findBlockInEditor, findTableRowInEditor])

    return {
        leftRef,
        rightRef
    }
}